import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────────
// Reusable brand ornament / shape components
// All use currentColor so they inherit `text-primary`, `text-muted-foreground` etc.
// ──────────────────────────────────────────────────────────────────────────────

export const CornerBrackets = ({ className = "", size = 28, stroke = 2 }) => (
  <>
    <span className={`absolute top-0 left-0 ${className}`} style={{ width: size, height: size, borderTop: `${stroke}px solid currentColor`, borderLeft: `${stroke}px solid currentColor`, borderTopLeftRadius: 8 }} />
    <span className={`absolute top-0 right-0 ${className}`} style={{ width: size, height: size, borderTop: `${stroke}px solid currentColor`, borderRight: `${stroke}px solid currentColor`, borderTopRightRadius: 8 }} />
    <span className={`absolute bottom-0 left-0 ${className}`} style={{ width: size, height: size, borderBottom: `${stroke}px solid currentColor`, borderLeft: `${stroke}px solid currentColor`, borderBottomLeftRadius: 8 }} />
    <span className={`absolute bottom-0 right-0 ${className}`} style={{ width: size, height: size, borderBottom: `${stroke}px solid currentColor`, borderRight: `${stroke}px solid currentColor`, borderBottomRightRadius: 8 }} />
  </>
);

export const HazardStripes = ({ className = "", angle = 135, thickness = 14, opacity = 0.15 }) => (
  <div className={`absolute inset-0 pointer-events-none ${className}`} style={{
    backgroundImage: `repeating-linear-gradient(${angle}deg, rgba(250,204,21,${opacity}) 0 ${thickness}px, transparent ${thickness}px ${thickness * 2}px)`
  }} />
);

export const BlueprintGrid = ({ className = "", size = 34, opacity = 0.18 }) => (
  <div className={`absolute inset-0 pointer-events-none ${className}`} style={{
    backgroundImage: `linear-gradient(rgba(250,204,21,${opacity}) 1px, transparent 1px), linear-gradient(90deg, rgba(250,204,21,${opacity}) 1px, transparent 1px)`,
    backgroundSize: `${size}px ${size}px`
  }} />
);

export const DottedMatrix = ({ className = "", size = 18, dot = 1.25 }) => (
  <div className={`absolute inset-0 pointer-events-none ${className}`} style={{
    backgroundImage: `radial-gradient(circle, rgba(250,204,21,0.35) ${dot}px, transparent ${dot + 0.5}px)`,
    backgroundSize: `${size}px ${size}px`
  }} />
);

export const ScanReticle = ({ size = 120, className = "" }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="60" cy="60" r="56" strokeDasharray="4 6" opacity="0.5" />
    <circle cx="60" cy="60" r="40" />
    <circle cx="60" cy="60" r="18" strokeWidth="2" />
    <circle cx="60" cy="60" r="2.5" fill="currentColor" />
    <line x1="60" y1="4" x2="60" y2="30" strokeWidth="2" />
    <line x1="60" y1="90" x2="60" y2="116" strokeWidth="2" />
    <line x1="4" y1="60" x2="30" y2="60" strokeWidth="2" />
    <line x1="90" y1="60" x2="116" y2="60" strokeWidth="2" />
  </svg>
);

export const BarcodeStrip = ({ className = "", height = 48, bars = 42 }) => {
  const widths = [2, 3, 1, 4, 2, 1, 3, 2, 5, 1, 2, 3, 2, 1, 4, 2, 3, 1, 2, 4, 1, 3, 2, 1, 5, 2, 1, 3, 2, 4, 1, 2, 3, 1, 4, 2, 1, 3, 2, 1, 5, 2];
  return (
    <div className={`flex items-stretch gap-[2px] ${className}`} style={{ height }}>
      {Array.from({ length: bars }).map((_, i) => (
        <div key={i} style={{ width: `${widths[i % widths.length]}px`, background: "currentColor" }} />
      ))}
    </div>
  );
};

export const HexPattern = ({ className = "", opacity = 0.18 }) => (
  <svg className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="hexpat" width="56" height="48" patternUnits="userSpaceOnUse" patternTransform="scale(1)">
        <path d="M28 0 L56 16 L56 32 L28 48 L0 32 L0 16 Z" fill="none" stroke="currentColor" strokeWidth="1" opacity={opacity} />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#hexpat)" />
  </svg>
);

export const CircuitTrace = ({ className = "" }) => (
  <svg viewBox="0 0 400 120" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M0 60 L60 60 L80 40 L160 40 L180 60 L260 60 L280 80 L400 80" />
    <circle cx="60" cy="60" r="4" fill="currentColor" />
    <circle cx="180" cy="60" r="4" fill="currentColor" />
    <circle cx="280" cy="80" r="4" fill="currentColor" />
    <path d="M160 40 L160 10 M260 60 L260 30" strokeDasharray="3 3" opacity="0.6" />
    <rect x="140" y="4" width="40" height="8" strokeWidth="1" />
    <rect x="240" y="24" width="40" height="8" strokeWidth="1" />
  </svg>
);

export const FingerprintMotif = ({ size = 160, className = "" }) => (
  <svg viewBox="0 0 160 160" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeLinecap="round">
    <g strokeWidth="1.3">
      <path d="M80 10 C36 10 12 46 12 80 C12 98 18 114 28 126" />
      <path d="M80 24 C44 24 24 54 24 80 C24 94 28 107 36 117" />
      <path d="M80 38 C52 38 36 60 36 80 C36 91 40 102 46 110" />
      <path d="M80 52 C60 52 48 66 48 80 C48 90 52 102 60 112" opacity="0.9" />
      <path d="M80 66 C68 66 60 74 60 84 C60 96 66 110 76 124" opacity="0.85" />
      <path d="M80 78 C76 80 74 84 76 92 C80 108 86 122 100 134" opacity="0.8" />
      <path d="M100 48 C118 58 128 78 128 96" />
      <path d="M112 36 C132 48 144 72 144 92 C144 104 140 116 132 126" />
    </g>
    <circle cx="80" cy="80" r="2" fill="currentColor" />
  </svg>
);

export const SerialTag = ({ children = "TIO-7K2-9XQR", className = "" }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 border border-current/40 rounded-sm bg-current/5 font-mono text-xs tracking-[0.15em] uppercase ${className}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current" />
    {children}
    <span className="w-3 h-px bg-current/60" />
  </div>
);

export const DiagonalRibbon = ({ className = "", text = "REGISTERED" }) => (
  <div className={`inline-block relative ${className}`}>
    <div className="px-6 py-1.5 font-heading font-black tracking-[0.2em] text-sm uppercase bg-current text-background"
         style={{ clipPath: 'polygon(8% 0, 100% 0, 92% 100%, 0% 100%)' }}>
      {text}
    </div>
  </div>
);

export const IndustrialStud = ({ size = 44, className = "" }) => (
  <svg viewBox="0 0 44 44" width={size} height={size} className={className} fill="none">
    <circle cx="22" cy="22" r="20" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    <circle cx="22" cy="22" r="12" fill="currentColor" opacity="0.15" />
    <circle cx="22" cy="22" r="12" stroke="currentColor" strokeWidth="1.2" />
    <circle cx="22" cy="22" r="3" fill="currentColor" />
    <path d="M22 10 L22 14 M22 30 L22 34 M10 22 L14 22 M30 22 L34 22" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

export const ToolSilhouette = ({ className = "" }) => (
  // Impact driver silhouette watermark
  <svg viewBox="0 0 240 160" className={className} fill="currentColor">
    <path d="M20 70 h110 a8 8 0 0 1 8 8 v14 a8 8 0 0 1 -8 8 h-110 a8 8 0 0 1 -8 -8 v-14 a8 8 0 0 1 8 -8z" opacity="0.9" />
    <path d="M70 100 v40 a8 8 0 0 0 8 8 h32 a8 8 0 0 0 8 -8 v-40 z" opacity="0.9" />
    <rect x="138" y="74" width="48" height="22" rx="4" opacity="0.9" />
    <rect x="186" y="80" width="40" height="10" opacity="0.9" />
    <polygon points="226,76 238,85 226,94" opacity="0.9" />
    <rect x="80" y="138" width="48" height="10" rx="3" opacity="0.9" />
    <circle cx="150" cy="85" r="6" fill="hsl(var(--background))" />
  </svg>
);

export const ScanBeam = ({ className = "" }) => (
  <div className={`relative w-full h-24 overflow-hidden rounded-md ${className}`}>
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-current/40 to-transparent" />
    <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[2px] bg-current shadow-[0_0_18px_currentColor]" />
  </div>
);

// ──────────────────────────────────────────────────────────────────────────────
// Brand Kit Showcase Page
// ──────────────────────────────────────────────────────────────────────────────

const Tile = ({ title, caption, children, contentClassName = "" }) => (
  <div className="relative overflow-hidden rounded-lg border border-primary/25 bg-card/50">
    <div className={`relative h-48 flex items-center justify-center overflow-hidden ${contentClassName}`}>
      {children}
    </div>
    <div className="px-4 py-3 border-t border-primary/10">
      <p className="font-heading uppercase tracking-wider text-sm">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{caption}</p>
    </div>
  </div>
);

export default function BrandKit() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="relative border-b border-primary/10 bg-background/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
          </div>
          <p className="font-heading uppercase tracking-[0.25em] text-xs text-muted-foreground">Brand Ornaments</p>
        </div>
      </header>

      {/* Intro */}
      <section className="relative overflow-hidden px-6 py-14 lg:px-12">
        <HazardStripes angle={135} thickness={16} opacity={0.05} />
        <div className="relative z-10 max-w-6xl mx-auto">
          <p className="font-mono text-xs tracking-[0.25em] text-primary/80 uppercase mb-3">TOOLIO · Visual System</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight mb-3">
            Brand <span className="text-primary">Ornaments</span> & Patterns
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            A kit of repeatable marks, shapes and textures — drop them into page backgrounds, section headers, hero frames, reports, PDFs, and share assets to keep the TOOLIO feel consistent wherever it shows up.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 pb-20 lg:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

          {/* 1. Corner Brackets */}
          <Tile title="01 · Corner Brackets" caption="Registered, verified, 'in-frame' feel">
            <div className="relative w-40 h-28 text-primary">
              <CornerBrackets size={22} stroke={2} />
              <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.2em] text-primary/80">TIO-7K2-9XQR</span>
            </div>
          </Tile>

          {/* 2. Hazard Stripes */}
          <Tile title="02 · Hazard Stripes" caption="Trade/industrial texture for headers & PDFs" contentClassName="">
            <div className="relative w-full h-full text-primary">
              <HazardStripes angle={135} thickness={16} opacity={0.22} />
              <span className="absolute inset-0 flex items-center justify-center font-heading uppercase tracking-[0.2em] text-xs text-muted-foreground">Workshop Tape</span>
            </div>
          </Tile>

          {/* 3. Blueprint Grid */}
          <Tile title="03 · Blueprint Grid" caption="Engineering/schematic background">
            <div className="relative w-full h-full text-primary">
              <BlueprintGrid size={30} opacity={0.28} />
              <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.2em] text-primary/70">BLUEPRINT · GRID</span>
            </div>
          </Tile>

          {/* 4. Dotted Matrix */}
          <Tile title="04 · Scanner Matrix" caption="Subtle QR-like dot field">
            <div className="relative w-full h-full text-primary">
              <DottedMatrix size={14} dot={1} />
              <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.2em] text-primary/60">CAPTURE · MATRIX</span>
            </div>
          </Tile>

          {/* 5. Scan Reticle */}
          <Tile title="05 · Scan Reticle" caption="Hero or document seal">
            <div className="text-primary relative">
              <ScanReticle size={130} />
              <span className="absolute inset-0 flex items-center justify-center font-heading uppercase text-[9px] tracking-widest mt-[84px] text-primary/80">Lock-On</span>
            </div>
          </Tile>

          {/* 6. Barcode */}
          <Tile title="06 · Barcode Strip" caption="Footer accents, cards, stickers">
            <div className="text-primary flex flex-col items-center gap-1.5">
              <BarcodeStrip height={46} bars={40} />
              <span className="font-mono text-[10px] tracking-[0.3em] text-primary/80">9 7 8 0 — T I O — 0 0 1</span>
            </div>
          </Tile>

          {/* 7. Hex Pattern */}
          <Tile title="07 · Hex Mesh" caption="Dashboard/tech backgrounds">
            <div className="relative w-full h-full text-primary">
              <HexPattern opacity={0.4} />
              <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.2em] text-primary/70">NETWORK · MESH</span>
            </div>
          </Tile>

          {/* 8. Circuit Trace */}
          <Tile title="08 · Circuit Trace" caption="Connected / police portal feel">
            <div className="text-primary/90 w-[80%]">
              <CircuitTrace className="w-full h-auto" />
            </div>
          </Tile>

          {/* 9. Fingerprint */}
          <Tile title="09 · Fingerprint" caption="'Every tool, a fingerprint' motif">
            <div className="text-primary">
              <FingerprintMotif size={150} />
            </div>
          </Tile>

          {/* 10. Serial Tag */}
          <Tile title="10 · Serial Tag" caption="Badge for UI & PDFs">
            <div className="flex flex-col items-center gap-3 text-primary">
              <SerialTag>TIO-7K2-9XQR</SerialTag>
              <SerialTag className="text-emerald-400">VERIFIED · LIVE</SerialTag>
              <SerialTag className="text-blue-400">POLICE · CONNECTED</SerialTag>
            </div>
          </Tile>

          {/* 11. Diagonal Ribbon */}
          <Tile title="11 · Diagonal Ribbon" caption="Status stamps for reports">
            <div className="flex flex-col items-center gap-2 text-primary">
              <DiagonalRibbon text="REGISTERED" />
              <DiagonalRibbon className="text-red-500" text="STOLEN" />
              <DiagonalRibbon className="text-emerald-400" text="RECOVERED" />
            </div>
          </Tile>

          {/* 12. Industrial Stud */}
          <Tile title="12 · Industrial Stud" caption="Corner rivet, divider dots">
            <div className="flex items-center gap-5 text-primary">
              <IndustrialStud size={48} />
              <IndustrialStud size={40} className="text-muted-foreground" />
              <IndustrialStud size={32} className="text-emerald-400" />
            </div>
          </Tile>

          {/* 13. Tool Silhouette */}
          <Tile title="13 · Tool Silhouette" caption="Decorative watermark behind cards">
            <div className="text-primary/70 w-[75%]">
              <ToolSilhouette className="w-full h-auto" />
            </div>
          </Tile>

          {/* 14. Scan Beam */}
          <Tile title="14 · Scan Beam" caption="Section dividers / loading states">
            <div className="text-primary w-4/5">
              <ScanBeam />
            </div>
          </Tile>

          {/* 15. Composite */}
          <Tile title="15 · Composite Frame" caption="Put it all together">
            <div className="relative w-56 h-36 text-primary rounded-md overflow-hidden border border-primary/30 bg-background/60">
              <BlueprintGrid size={22} opacity={0.2} />
              <HazardStripes angle={135} thickness={10} opacity={0.06} />
              <CornerBrackets size={18} stroke={2} />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <FingerprintMotif size={60} />
                <span className="font-mono text-[9px] tracking-[0.25em] text-primary/80">TIO · REGISTRY</span>
              </div>
            </div>
          </Tile>
        </div>

        {/* Notes */}
        <div className="max-w-6xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
          <div className="p-4 border border-primary/20 rounded-md bg-card/40">
            <p className="font-heading uppercase tracking-[0.18em] text-xs text-primary mb-1">Web</p>
            <p className="text-muted-foreground">Use patterns at 8–30% opacity behind sections. Keep foreground legible.</p>
          </div>
          <div className="p-4 border border-primary/20 rounded-md bg-card/40">
            <p className="font-heading uppercase tracking-[0.18em] text-xs text-primary mb-1">Documents / PDF</p>
            <p className="text-muted-foreground">Blueprint grid + hazard stripes as page headers; corner brackets for tool summaries; ribbons for status.</p>
          </div>
          <div className="p-4 border border-primary/20 rounded-md bg-card/40">
            <p className="font-heading uppercase tracking-[0.18em] text-xs text-primary mb-1">Social / Share</p>
            <p className="text-muted-foreground">Fingerprint + scan reticle + serial tag = instantly recognisable TOOLIO lock-ups.</p>
          </div>
        </div>

        {/* Brand Photography */}
        <div className="max-w-6xl mx-auto mt-20">
          <div className="mb-8">
            <p className="font-mono text-xs tracking-[0.25em] text-primary/80 uppercase mb-2">TOOLIO · Photography</p>
            <h2 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
              Brand <span className="text-primary">Photography</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mt-3">
              Scene-based marketing imagery — for landing heroes, marketing pages, social posts, insurance reports and press kits.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { file: "01-tradesman-scanning-drill.png", title: "Tradesman · Scan", caption: "Workshop, DeWalt-style impact driver" },
              { file: "02-builder-on-site-scanning.png", title: "Builder · On Site", caption: "Crate of tools, construction context" },
              { file: "03-police-officer-scanning.png", title: "Police · Recovery", caption: "Officer scanning a recovered drill" },
              { file: "04-phone-ui-in-hand.png", title: "App · In Hand", caption: "Close-up of app on a phone" },
              { file: "05-hero-tool-with-scan-overlay.png", title: "Hero · Product", caption: "Studio tool with scan beam" },
              { file: "06-van-inventory-scan.png", title: "Van · Dawn Inventory", caption: "Tradesman registering his van load" },
              { file: "07-insurer-reviewing-report.png", title: "Insurer · Report", caption: "Clean, credible claim workflow" },
              { file: "08-two-hands-tool-handover.png", title: "Handover · Verified", caption: "Used-tool buyer checks with phone" },
            ].map((p) => (
              <a key={p.file} href={`/brandkit/${p.file}`} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-primary/25 bg-card/50 hover:border-primary/60 transition-colors group" data-testid={`brand-photo-${p.file}`}>
                <div className="aspect-video overflow-hidden bg-black">
                  <img src={`/brandkit/${p.file}`} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                </div>
                <div className="px-4 py-3 border-t border-primary/10">
                  <p className="font-heading uppercase tracking-wider text-sm">{p.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.caption}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
